import { SetStateAction, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import Dropdown from "components/dropdown";
import { FaCheck } from "react-icons/fa";
import { RiFileTextLine } from "react-icons/ri";
import ToggleSwitch from "components/toggleSwitch";
import { Button } from "components/button";
import MultiInput from "components/input/multi";
import DatePicker from "react-datepicker";
import { enUS, es } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useSnackbarContext } from "components/snackbar";
import { IPropertySelected } from "../interface";
import { IoCloseCircleOutline } from "react-icons/io5";

interface IProps {
  selectedPlans: any[];
  setSelectedPlans?: (plans: any[]) => void;
  setModal: () => void;
  setButtonSelection: (selection: string) => void;
};

const CHUNK_SIZE = 10;

const SendPlansModal = ({ selectedPlans, setSelectedPlans, setModal, setButtonSelection }: IProps) => {
  const { t, i18n } = useTranslation();
  const tBase = "views.service-plan-dashboard.components.SendPlansModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const locale = i18n.language === "es" ? es : enUS;

  const { showSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();

  const [hasContract, setHasContract] = useState(false);
  const [selected, setSelected] = useState<any[]>(selectedPlans || []);
  const [servicePlan, setServicePlan] = useState<any>(null);
  const [dateOption, setDateOption] = useState("immediately");
  const [startDate, setStartDate] = useState(new Date().toISOString());
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState("");
  const [sendResults, setSendResults] = useState<{
    total: number;
    completed: number;
    successful: number;
    failed: number;
  }>({
    total: 0,
    completed: 0,
    successful: 0,
    failed: 0,
  });
  const [conflictingPropertyIds, setConflictingPropertyIds] = useState<string[]>([]);
  const [planEmails, setPlanEmails] = useState<Record<string, string[]>>({});
  const [sendComplete, setSendComplete] = useState(false);
  const [action, setAction] = useState("send");

  useEffect(() => {
    setDateOption(servicePlan?.start_date_type || "immediately");
  }, [servicePlan]);

  const { data: plansData } = useQuery("plans", async () => {
    return await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin?type=PLANS`)
      .then((res) => res.data)
  });

  const { data: conflictData } = useQuery(
    ["check-existing-plans", servicePlan?.id, selected],
    async () => {
      if (!servicePlan?.id || selected.length === 0) return [];
      
      return await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/check-existing-plans`,
        {
          servicePlanId: servicePlan.id,
          propertyIds: selected.map(plan => plan.property.id),
        }
      ).then(res => res.data ?? []);
    },
    {
      enabled: !!servicePlan?.id && selected.length > 0 && !sending && action === "send",
    }
  );

  useEffect(() => {
    if (conflictData) {
      setConflictingPropertyIds(conflictData);
    }
  }, [conflictData]);

  const sendContractChunk = useMutation(
    async (info: {
      servicePlanId?: string;
      startDate?: string;
      properties: IPropertySelected[];
      action: string;
    }): Promise<{ successful: number; failed: number }> => {
      const type = action === "resend" ? "resend" : (hasContract ? "send" : "add");
      // For resend, we need to send the plan_id (sptc_id) instead of servicePlanId
      const payload = action === "resend"
        ? { data: { properties: info.properties } }
        : { data: { servicePlanId: info.servicePlanId, startDate: info.startDate, properties: info.properties } };
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/send-contract-bulk/${type}`, 
        payload
      );
      return response.data;
    }
  );

  const preparePropertiesForSending = () => {
    return selected
      .filter(plan => !conflictingPropertyIds.includes(plan.property.id))
      .map(plan => ({
        plan_id: plan.sptc_id,
        id: plan.property.id,
        customer_id: plan.customer_id,
        address: plan.property.address,
        city: plan.property.city,
        state: plan.property.state,
        zipcode: plan.property.zipcode,
        emails: hasContract ? (planEmails[plan.sptc_id] || [plan.email] || []) : [],
        conflict: false // Already filtered out conflicts
      }));
  };

  // Chunked send function
  const handleChunkedSend = async () => {
    if (action !== "resend" && (!servicePlan || !startDate)) return;
    setSending(true);
    setSendProgress("");
    
    // Filter out conflicting properties
    let propertiesToSend;
    if (action === "resend") {
      // Filter out non contract plans for resend
      propertiesToSend = selected
        .filter(plan => (plan.is_contract))
        .map(plan => ({
          plan_id: plan.sptc_id,
          service_plan_id: plan.service_plan_id,
          id: plan.property.id,
          customer_id: plan.customer_id,
          address: plan.property.address,
          city: plan.property.city,
          state: plan.property.state,
          zipcode: plan.property.zipcode,
          emails: planEmails[plan.sptc_id] || [plan.email] || [],
          customer_name: plan.customer_name,
          property_name: plan.property.name,
        }));
    } else {
      // For send, filter out conflicting properties
      propertiesToSend = preparePropertiesForSending();
    }

    const totalProperties = propertiesToSend.length;
    
    // Initialize results
    const results = {
      total: totalProperties,
      completed: 0,
      successful: 0,
      failed: 0,
    };
    setSendResults(results);

    if (totalProperties === 0) {
      setSending(false);
      showSnackbar("No properties to send", "error");
      return;
    }

    // Split into chunks
    const chunks = [];
    for (let i = 0; i < propertiesToSend.length; i += CHUNK_SIZE) {
      chunks.push(propertiesToSend.slice(i, i + CHUNK_SIZE));
    }

    // Process chunks
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const chunkNumber = i + 1;
      const totalChunks = chunks.length;
      
      setSendProgress(`Processing batch ${chunkNumber} of ${totalChunks} (${chunk.length} properties)...`);
      
      try {
        const chunkResult = await sendContractChunk.mutateAsync({
          ...(action !== "resend" && { servicePlanId: servicePlan.id, startDate }),
          properties: chunk,
          action
        });

        
        // Update success count
        results.successful += chunkResult.successful;
        results.failed += chunkResult.failed;
        
      } catch (error) {
        console.error(`Error in chunk ${chunkNumber}:`, error);
        // If the entire chunk failed, mark all as failed
        results.failed += chunk.length;
      } finally {
        // Update progress

        results.completed += chunk.length;
        setSendResults({...results});
        setSendProgress(`Completed ${results.completed} of ${totalProperties} properties...`);
      }
  
    }

    // Final results
    setSending(false);
    setSendComplete(true);
    queryClient.invalidateQueries();
    
    if (results.successful > 0) {
      const isContract = servicePlan?.is_contract;
      const message = isContract 
        ? `${results.successful} ${tr("contracts sent successfully")}` 
        : `${results.successful} ${tr("service plans added successfully")}`;
      
      if (results.failed > 0) {
        showSnackbar(`${message}. ${results.failed} failed.`, "warning");
      } else {
        showSnackbar(message, "success");
      }
    } else {
      showSnackbar(tr("All sends failed. Please check the errors."), "error");
    }
  };


 const handleRemovePlan = (planId: string) => {
    if (setSelectedPlans) {
      const newPlans = selected.filter(plan => plan.sptc_id !== planId);
      setSelectedPlans(newPlans);
      setSelected(newPlans)
    }
  };

  const handleRemoveConflicts = () => {
    if (setSelectedPlans) {
      const nonConflictingPlans = selected.filter(plan => 
        !conflictingPropertyIds.includes(plan.property.id)
      );
      
      const removedCount = selected.length - nonConflictingPlans.length;
      setSelectedPlans(nonConflictingPlans);
      setSelected(nonConflictingPlans);
      
      if (removedCount > 0) {
        showSnackbar(`Removed ${removedCount} conflicting ${removedCount === 1 ? 'property' : 'properties'}`, "info");
      }
    }
  };

  const handleEmailChange = (planId: string, emails: any) => {
    setPlanEmails(prev => ({
      ...prev,
      [planId]: emails
    }));
  };

  const activePlans = plansData?.filter((plan: any) => !plan.archived) || [];
  const conflictingPlansCount = selected.filter(plan => conflictingPropertyIds.includes(plan.property.id)).length;
  const changeWidth = (action === "send" && hasContract) || (action === "resend" && selected.some(plan => plan.is_contract));
  const plansWidth = changeWidth ? "w-[580px]" : "w-[320px]";
  // Count contract plans for resend
  const contractPlansCount = selected.filter(plan => plan.is_contract).length;

  return (
    <div className={`
      flex justify-between p-4 relative h-[80vh]
    `}>
      {/* Selected Plans */}
      <div className={plansWidth}>
        <div className="absolute top-0 z-20">
          <div className={`
            fixed bg-white border-b p-2 text-lg font-semibold shadow-sm text-primary
            ${plansWidth}
          `}>
            {tr("Selected Plans")} {selected.length > 0 ? `(${selected.length})` : ""}
          </div>
        </div>
        <div className="mt-[30px] border-r space-y-1">
          {selected.map((plan, i) => {
            const hasConflict = servicePlan && conflictingPropertyIds.includes(plan.property.id);

            return (
              <div 
                key={plan.sptc_id} 
                className={`
                  py-2 pl-2 pr-6 border-b flex justify-between items-center rounded-md relative
                  ${hasConflict && !sendComplete && action === "send" ? "bg-red-100" : "bg-white"}
                `}
              >
                <div className="flex items-center justify-start space-x-2">
                  <div>{`${i+1}.`}</div>
                  <div className="text-gray-700 text-sm flex-wrap w-[250px]">
                    <div className="text-primary">
                      {plan.plan_name}
                    </div>
                    <div>
                      {plan.customer_name}
                    </div>
                    <div>
                      {plan.property.address} {plan.property.city} {plan.property.state} {plan.property.zipcode}
                    </div>
                  </div>
                </div>
                {((action === "send" && hasContract) || (action === "resend" && plan.is_contract)) && (
                  <div className="ml-2 mr-4 w-[250px]">
                    <MultiInput
                      values={planEmails[plan.sptc_id] || [plan.email] || []}
                      onChange={(emails) => handleEmailChange(plan.sptc_id, emails)}
                    />
                  </div>
                )}
                <button 
                  className="absolute top-[calc(50%_-_10px)] right-[10px] text-red-500 hover:bg-red-500 hover:text-white rounded-full"
                  onClick={() => handleRemovePlan(plan.sptc_id)}
                >
                  <IoCloseCircleOutline size={20} />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Options */}
      <div className="w-[350px]">
        <div className="fixed w-[350px]">
          {/* Header */}
          <div className="border-b px-4 pb-2 -mt-3 shadow-sm">
            <div className="flex justify-between items-center">
              <div className=" text-lg font-semibold text-secondary">
                {tr("New Plan")}
              </div>
              <ToggleSwitch
                  options={[
                    { value: "send", label: tr("Send") },
                    { value: "resend", label: tr("Resend") },
                  ]}
                  defaultValue={action}
                  onChange={(value) => {
                    setAction(value);
                  }}
                />
            </div>
          </div>
          {/* Send / Resend Content */}
          <div className="flex flex-col space-y-6 mx-4 mt-4">

            {/* Send option buttons */}
            {action === "send" && (
              <>
                <div>
                  <Dropdown
                    reference={
                      <button className="h-10 w-full bg-white border border-gray-300 rounded-md shadow-sm text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 px-2">
                        {servicePlan ? (
                          <div className="relative">
                            <div className="flex flex-row justify-center items-center space-x-1">
                              <div>
                                {servicePlan.name}
                              </div>
                              <div>
                                {hasContract && (
                                  <RiFileTextLine size={22} className="text-primary" />
                                )}
                              </div>
                            </div>
                            <div className="absolute top-[-20px] right-[-20px] bg-white rounded-full border border-gray-300 p-0.5">
                              <FaCheck size={18} className="text-green-500" />
                            </div>
                          </div>
                        ) : (
                          tr("Select Plan")
                        )}
                      </button>
                    }
                    data={[
                      {
                        button: activePlans?.map(
                          (x: {
                            is_contract: boolean;
                            name: string;
                            id: SetStateAction<string | number | undefined>;
                            start_date_type: string;
                          }, i:number ) => {
                            if (x.is_contract === true) {
                              return {
                                label: (
                                  <div key={i} className="flex flex-row justify-between items-center space-x-1">
                                    <div>{x.name}</div>
                                    <RiFileTextLine size={22} className="text-primary" />
                                  </div>
                                ),
                                onClick: () => {
                                  setServicePlan({
                                    id: String(x.id),
                                    name: x.name,
                                    start_date_type: x.start_date_type,
                                  });
                                  setHasContract(x.is_contract);
                                },
                              };
                            }
                            return {
                              label: x.name,
                              onClick: () => {
                                setServicePlan({
                                  id: String(x.id),
                                  name: x.name,
                                  start_date_type: x.start_date_type,
                                });
                                setHasContract(x.is_contract);
                              },
                            };
                          }
                        ),
                      },
                    ]}
                  />
                </div>
                <div>
                  <ToggleSwitch
                    options={[
                      { value: "immediately", label: tr("Immediately") },
                      { value: "new_year", label: tr("January 1st"), tooltip: `${tr("January 1st")}, ${new Date().getFullYear()}` },
                      { value: "custom", label: tr("Custom") },
                    ]}
                    defaultValue={dateOption}
                    onChange={(value) => {
                      setDateOption(value);

                      if (value === "immediately") {
                        const immediately = new Date();
                        setStartDate(immediately.toISOString());
                      } else if (value === "new_year") {
                        const currentYear = new Date();
                        currentYear.setFullYear(currentYear.getFullYear());
                        currentYear.setMonth(0);
                        currentYear.setDate(1);
                        setStartDate(currentYear.toISOString());
                      } 
                    }}
                  />
                  {dateOption === "custom" && (
                    <div className="mt-2 -mb-4">
                      <DatePicker
                        className="h-10 w-full bg-white border border-gray-300 rounded-md shadow-sm text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 px-2"
                        selected={new Date(startDate)}
                        onChange={(date) => {
                          if (date && date instanceof Date) {
                            // Set time to noon to avoid timezone issues
                            date.setHours(12, 0, 0, 0);
                            setStartDate(date.toISOString());
                          }
                        }}
                        popperPlacement="bottom"
                        locale={locale}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Error Display */}
            {/* {sendResults.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-3 max-h-20 overflow-y-auto">
                <div className="text-xs text-red-700">
                  <div className="font-medium mb-1">{tr("Errors")}:</div>
                  {sendResults.errors.map((error, i) => (
                    <div key={i} className="truncate">{error}</div>
                  ))}
                </div>
              </div>
            )} */}

                       {/* Resend Info */}
            {/* {action === "resend" && contractPlansCount < selected.length && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="text-sm text-blue-700">
                  {tr("Only contract plans can be resent")}
                  <div className="text-xs mt-1">
                    {contractPlansCount} {tr("of")} {selected.length} {tr("selected plans are contracts")}
                  </div>
                </div>
              </div>
            )} */}

            {/* Send Button / Conflicting Message */}
            <div className="flex items-center justify-between pl-1">
              <div>
                {action === "send" && conflictingPlansCount > 0 && !sending && !sendComplete && (
                  <div className="text-xs text-red-500 mr-2">
                    {tr("Conflicting properties ")}
                    <br />
                    {tr("will not be sent.")}
                  </div>
                )}
              </div>
              {!sendComplete ? (
                <Button
                  primary
                  onClick={handleChunkedSend}
                  disabled={
                    sending || 
                    selected.length === 0 || 
                    (action === "send" && (!servicePlan || !startDate)) ||
                    (action === "resend" && contractPlansCount === 0)
                  }
                >
                  {action === "send" 
                    ? sending 
                      ? tr("Sending...") 
                      : tr("Send Plans")
                    : sending // resend
                      ? tr("Resending...") 
                      : `${tr("Resend Plans")} (${contractPlansCount})`}
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setSelected([]);
                    setSelectedPlans && setSelectedPlans([]);
                    setServicePlan(null);
                    setSendComplete(false);
                    setModal(); 
                    setButtonSelection && setButtonSelection("");
                  }}
                >
                  {tr("Close")}
                </Button>
              )}
            </div>

            {/* Remove Conflicts Button */}
            {action === "send" && conflictingPlansCount > 0 && !sending && !sendComplete && (
              <Button 
                onClick={handleRemoveConflicts}
                className="w-full text-red-600"
              >
                {tr("Remove Conflicting Properties")} ({conflictingPlansCount})
              </Button>
            )}
            
            {/* Progress Display */}
            {(sending || sendProgress) && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="text-sm text-blue-700">
                  {sendProgress || "Processing..."}
                </div>
                {sendResults.total > 0 && (
                  <div className="mt-2 text-xs">
                    <div className="flex justify-between">
                      <span>{tr("Successful")}:</span>
                      <span className="text-green-600">{sendResults.successful}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{tr("Failed")}:</span>
                      <span className="text-red-600">{sendResults.failed}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>{tr("Total")}:</span>
                      <span>{sendResults.completed}/{sendResults.total}</span>
                    </div>
                  </div>
                )}
                {/* Progress bar (only show while sending) */}
                {(sending || sendProgress) && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(sendResults.completed / sendResults.total) * 100}%` 
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}

export default SendPlansModal